// src/services/firestoreService.js
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  setDoc,
  query,
  limit,
  where,
  orderBy,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { storage } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

class FirestoreService {

  cleanData(data) {
    const cleaned = {};
    Object.keys(data).forEach(key => {
      if (data[key] !== undefined) {
        if (data[key] !== null && typeof data[key] === 'object' && !Array.isArray(data[key]) && !(data[key] instanceof Date)) {
          cleaned[key] = this.cleanData(data[key]);
        } else {
          cleaned[key] = data[key];
        }
      }
    });
    return cleaned;
  }

  async getDocument(collectionName, docId) {
    try {
      if (!db) throw new Error('Firestore is not initialized');
      const docSnap = await getDoc(doc(db, collectionName, docId));
      return docSnap;
    } catch (error) {
      console.error(`Error getting document from ${collectionName}:`, error);
      throw error;
    }
  }

  async updateDocument(collectionName, docId, data) {
    try {
      if (!db) throw new Error('Firestore is not initialized');
      const cleanedData = this.cleanData(data);
      await updateDoc(doc(db, collectionName, docId), cleanedData);
      return true;
    } catch (error) {
      console.error(`Error updating document in ${collectionName}:`, error);
      throw error;
    }
  }

  async deleteDocument(collectionName, docId) {
    try {
      if (!db) throw new Error('Firestore is not initialized');
      await deleteDoc(doc(db, collectionName, docId));
      return true;
    } catch (error) {
      console.error(`Error deleting document from ${collectionName}:`, error);
      throw error;
    }
  }

  async getUser(userId) {
    try {
      if (!db) {
        throw new Error('Firestore is not initialized');
      }

      const userDoc = await getDoc(doc(db, 'pb-users', userId));

      if (userDoc.exists()) {
        return { id: userDoc.id, ...userDoc.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting user:', error);
      throw error;
    }
  }

  async updateUser(userId, userData) {
    try {
      if (!db) {
        throw new Error('Firestore is not initialized');
      }

      const cleanedData = this.cleanData(userData);
      const userRef = doc(db, 'pb-users', userId);

      await updateDoc(userRef, cleanedData);

      console.log('User updated successfully:', userId);
      return await this.getUser(userId);
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  async createUser(userId, userData) {
    try {
      if (!db) {
        throw new Error('Firestore is not initialized');
      }

      const cleanedData = this.cleanData(userData);
      const userRef = doc(db, 'pb-users', userId);

      await setDoc(userRef, {
        ...cleanedData,
        createdAt: new Date(),
        updatedAt: new Date()
      });

      console.log('User created successfully:', userId);
      return await this.getUser(userId);
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  async getUserEmail(userId) {
    try {
      const userDoc = await getDoc(doc(db, 'pb-users', userId));
      return userDoc.exists() ? userDoc.data()?.email || null : null;
    } catch (error) {
      console.error('Error getting user email:', error);
      return null;
    }
  }

  // ============ ORGANISATION MANAGEMENT METHODS ============

  async createOrganisation(orgData, userId) {
    try {
      if (!db) {
        throw new Error('Firestore is not initialized');
      }

      const cleanedData = this.cleanData(orgData);

      // Create organisation document
      const orgRef = await addDoc(collection(db, 'pb-organisations'), {
        ...cleanedData,
        createdAt: new Date(),
        updatedAt: new Date()
      });

      // Add organisation ID to user's organisationIds array
      const userRef = doc(db, 'pb-users', userId);
      const userDoc = await getDoc(userRef);
      const currentOrgIds = userDoc.data()?.organisationIds || [];

      await updateDoc(userRef, {
        organisationIds: [...currentOrgIds, orgRef.id],
        updatedAt: new Date()
      });

      console.log('Organisation created successfully:', orgRef.id);
      return orgRef.id;
    } catch (error) {
      console.error('Error creating organisation:', error);
      throw error;
    }
  }

  async getOrganisation(orgId) {
    try {
      if (!db) {
        throw new Error('Firestore is not initialized');
      }

      const orgDoc = await getDoc(doc(db, 'pb-organisations', orgId));

      if (orgDoc.exists()) {
        return { id: orgDoc.id, ...orgDoc.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting organisation:', error);
      throw error;
    }
  }

  async getUserOrganisations(userId) {
    try {
      if (!db) {
        throw new Error('Firestore is not initialized');
      }

      const q = query(
        collection(db, 'pb-organisations'),
        where('ownerId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting user organisations:', error);
      return [];
    }
  }

  async updateOrganisation(orgId, orgData) {
    try {
      if (!db) {
        throw new Error('Firestore is not initialized');
      }

      const cleanedData = this.cleanData(orgData);
      const orgRef = doc(db, 'pb-organisations', orgId);

      await updateDoc(orgRef, {
        ...cleanedData,
        updatedAt: new Date()
      });

      console.log('Organisation updated successfully:', orgId);
      return await this.getOrganisation(orgId);
    } catch (error) {
      console.error('Error updating organisation:', error);
      throw error;
    }
  }

  async deleteOrganisation(orgId, userId) {
    try {
      if (!db) {
        throw new Error('Firestore is not initialized');
      }

      // Remove organisation ID from user's organisationIds array
      const userRef = doc(db, 'pb-users', userId);
      const userDoc = await getDoc(userRef);
      const currentOrgIds = userDoc.data()?.organisationIds || [];

      await updateDoc(userRef, {
        organisationIds: currentOrgIds.filter(id => id !== orgId),
        updatedAt: new Date()
      });

      // Delete the organisation document
      await deleteDoc(doc(db, 'pb-organisations', orgId));

      console.log('Organisation deleted successfully:', orgId);
      return true;
    } catch (error) {
      console.error('Error deleting organisation:', error);
      throw error;
    }
  }

  async getOrganisationsByIds(orgIds) {
    try {
      if (!db || !orgIds || orgIds.length === 0) {
        return [];
      }

      const organisations = await Promise.all(
        orgIds.map(id => this.getOrganisation(id))
      );

      return organisations.filter(org => org !== null);
    } catch (error) {
      console.error('Error getting organisations by IDs:', error);
      return [];
    }
  }
  // Book management
  async createBook(bookData, options = {}) {
    try {
      console.log('Creating book with data:', bookData);

      if (!db) {
        throw new Error('Firestore is not initialized');
      }

      // Enhance book data with category-specific settings if provided
      let enhancedBookData = { ...bookData };

      if (options.category || bookData.category) {
        const category = options.category || bookData.category;
        const categoryConfig = this.getBookCategoryConfig(category);

        enhancedBookData = {
          ...bookData,
          category,
          settings: {
            ...categoryConfig,
            ...bookData.settings // User settings override defaults
          }
        };
      }

      const docRef = await addDoc(collection(db, 'pb-books'), enhancedBookData);
      console.log('Book created with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error creating book:', error);
      throw error;
    }
  }

  async duplicateBook(bookId, userId) {
    try {
      const bookRef = doc(db, 'pb-books', bookId);
      const bookSnap = await getDoc(bookRef);

      if (!bookSnap.exists()) {
        throw new Error('Book not found');
      }

      const originalBook = bookSnap.data();
      const { id, ...bookDataWithoutId } = originalBook;

      const newBookData = {
        ...bookDataWithoutId,
        botId: `book_${Date.now()}`,
        botName: `${originalBook.botName} (Copy)`,
        ownerId: userId,
        published: false,
        accessLevel: 'private',
        viewCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const newBookRef = await addDoc(collection(db, 'pb-books'), newBookData);
      const newBookId = newBookRef.id;

      const messagesRef = collection(db, 'pb-books', bookId, 'messages');
      const messagesSnap = await getDocs(messagesRef);

      if (!messagesSnap.empty) {
        const batch = writeBatch(db);

        messagesSnap.docs.forEach((msgDoc) => {
          const { id: msgId, ...messageData } = msgDoc.data();
          const newMessageRef = doc(collection(db, 'pb-books', newBookId, 'messages'));

          batch.set(newMessageRef, {
            ...messageData,
            bookId: newBookId,
            botId: newBookData.botId,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        });

        await batch.commit();
      }

      return { id: newBookId, ...newBookData };
    } catch (error) {
      console.error('Error duplicating book:', error);
      throw error;
    }
  }

  getBookCategoryConfig(category) {
    const configs = {
      education: {
        defaultScoring: true,
        gradingEnabled: true,
        analyticsLevel: 'detailed',
        allowResubmission: false,
        showCorrectAnswers: 'after_submission'
      },
      quiz: {
        defaultScoring: true,
        gradingEnabled: true,
        analyticsLevel: 'basic',
        allowResubmission: false,
        showCorrectAnswers: 'after_deadline',
        timeLimit: 30
      },
      survey: {
        defaultScoring: false,
        gradingEnabled: false,
        analyticsLevel: 'aggregate',
        allowResubmission: true,
        showCorrectAnswers: 'never',
        anonymous: true
      },
      events: {
        defaultScoring: false,
        gradingEnabled: false,
        analyticsLevel: 'detailed',
        allowResubmission: true,
        showCorrectAnswers: 'never',
        eventManagement: true,
        bookingEnabled: true,
        paymentProcessing: true,
        giftRegistryEnabled: true,
        supplierManagement: true,
        checkInSystem: true,
        galleryManagement: true
      }
    };

    return configs[category] || {};
  }

  async getBook(bookId) {
    try {
      if (!db) {
        throw new Error('Firestore is not initialized');
      }

      const docSnap = await getDoc(doc(db, 'pb-books', bookId));
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting book:', error);
      throw error;
    }
  }

  async updateBook(bookId, bookData) {
    try {
      if (!db) {
        throw new Error('Firestore is not initialized');
      }

      await updateDoc(doc(db, 'pb-books', bookId), {
        ...bookData,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating book:', error);
      throw error;
    }
  }

  // Message management (CSV-compatible)
  async batchCreateMessages(messages, options = {}) {
    try {
      console.log('Creating messages:', messages);

      if (!db) {
        throw new Error('Firestore is not initialized');
      }

      if (!messages || messages.length === 0) {
        console.log('No messages to create');
        return [];
      }

      // Enhance messages if category is specified
      const enhancedMessages = options.enhanceWithCategory
        ? messages.map(message => this.enhanceMessage(message, options.category))
        : messages;

      const batch = writeBatch(db);
      const refs = [];

      enhancedMessages.forEach(message => {
        const docRef = doc(collection(db, 'pb-messages'));
        batch.set(docRef, message);
        refs.push(docRef);
      });

      await batch.commit();
      console.log('Messages created successfully');
      return refs.map(ref => ref.id);
    } catch (error) {
      console.error('Error creating messages:', error);
      throw error;
    }
  }

  // NEW: Enhance individual message with metadata and scoring
  enhanceMessage(message, category = 'education') {
    // Don't enhance if already enhanced
    if (message.metadata && message.scoring) {
      return message;
    }

    const baseEnhancement = {
      ...message,
      category: category,
    };

    // Add metadata template if not present
    if (!message.metadata) {
      baseEnhancement.metadata = this.getMetadataTemplate(category);
    }

    // Add scoring template if interactive element and not present
    if (message.userResponseType && !message.scoring) {
      baseEnhancement.scoring = this.getScoringTemplate(message.userResponseType);
    }

    // Add assessment config if not present
    if (!message.assessment) {
      baseEnhancement.assessment = this.getAssessmentTemplate(category);
    }

    return baseEnhancement;
  }

  // NEW: Metadata templates
  getMetadataTemplate(category) {
    const templates = {
      education: {
        topics: [],
        subtopics: [],
        tags: [],
        learningObjectives: [],
        knowledgeLevel: 'understand',
        difficulty: 'medium',
        estimatedTime: 5,
        prerequisites: []
      },
      quiz: {
        topics: [],
        difficulty: 'medium',
        estimatedTime: 2,
        quizType: 'formative'
      },
      survey: {
        surveyType: 'feedback',
        demographic: false,
        anonymous: true,
        estimatedTime: 1
      }
    };

    return templates[category] || templates['education'];
  }

  // NEW: Scoring templates
  getScoringTemplate(userResponseType) {
    const responseType = userResponseType.split('(')[0];

    const templates = {
      'math': {
        enabled: true,
        type: 'question',
        maxPoints: 10,
        scoringMethod: 'partial',
        correctAnswer: {
          value: '',
          acceptableVariations: [],
          partialCreditRules: []
        }
      },
      'multiselect': {
        enabled: true,
        type: 'option',
        maxPoints: 10,
        scoringMethod: 'weighted',
        optionScoring: []
      },
      'choice': {
        enabled: true,
        type: 'option',
        maxPoints: 5,
        scoringMethod: 'exact',
        optionScoring: []
      },
      'rating': {
        enabled: false,
        type: 'feedback',
        maxPoints: 0
      },
      'text': {
        enabled: true,
        type: 'rubric',
        maxPoints: 10,
        scoringMethod: 'rubric'
      }
    };

    return templates[responseType] || { enabled: false };
  }

  // NEW: Assessment templates
  getAssessmentTemplate(category) {
    return {
      isGraded: category === 'education' || category === 'quiz',
      autoGrade: true,
      showCorrectAnswer: 'after_submission',
      allowMultipleAttempts: false,
      maxAttempts: 1,
      feedback: {
        immediate: '',
        incorrect: '',
        partial: '',
        hints: []
      }
    };
  }

  // NEW: Update message with metadata and scoring
  async updateMessageEnhancement(messageId, metadata = null, scoring = null, assessment = null) {
    try {
      const updateData = {};

      if (metadata) updateData.metadata = metadata;
      if (scoring) updateData.scoring = scoring;
      if (assessment) updateData.assessment = assessment;

      updateData.updatedAt = new Date();

      await updateDoc(doc(db, 'pb-messages', messageId), updateData);
      return updateData;
    } catch (error) {
      console.error('Error updating message enhancement:', error);
      throw error;
    }
  }

  async updateMessageConfiguration(firestoreDocId, config) {
    try {
      if (!firestoreDocId) {
        throw new Error('No Firestore document ID provided');
      }

      const updateData = {
        updatedAt: new Date()
      };

      if (config.metadata) updateData.metadata = config.metadata;
      if (config.scoring) updateData.scoring = config.scoring;
      if (config.assessment) updateData.assessment = config.assessment;
      if (config.analytics) updateData.analytics = config.analytics;

      // Use the correct Firestore document ID
      const cleanedData = this.cleanData(updateData);
      await updateDoc(doc(db, 'pb-messages', firestoreDocId), cleanedData);

      console.log('Message configuration updated successfully:', firestoreDocId);
      return cleanedData;
    } catch (error) {
      console.error('Error updating message configuration:', error);
      throw error;
    }
  }

  // NEW: Batch create enhanced book with messages
  async createBookWithMessages(bookData, messagesData, category = 'education') {
    try {
      // Create book with category
      const bookId = await this.createBook(bookData, { category });

      // Create enhanced messages if provided
      if (messagesData && messagesData.length > 0) {
        const messagesWithBookId = messagesData.map(message => ({
          ...message,
          bookId
        }));

        await this.batchCreateMessages(messagesWithBookId, {
          enhanceWithCategory: true,
          category
        });
      }

      return bookId;
    } catch (error) {
      console.error('Error creating book with messages:', error);
      throw error;
    }
  }

  // NEW: Scoring functionality
  async scoreResponse(messageConfig, userResponse) {
    if (!messageConfig.scoring?.enabled) {
      return { score: 0, maxPoints: 0, feedback: null };
    }

    const scoring = messageConfig.scoring;

    switch (scoring.type) {
      case 'question':
        return this.scoreQuestion(scoring, userResponse);
      case 'option':
        return this.scoreOptions(scoring, userResponse);
      case 'partial':
        return this.scorePartial(scoring, userResponse);
      default:
        return { score: 0, maxPoints: scoring.maxPoints || 0 };
    }
  }

  scoreQuestion(scoring, userResponse) {
    const { correctAnswer, maxPoints } = scoring;

    if (userResponse === correctAnswer.value) {
      return { score: maxPoints, maxPoints, feedback: 'Correct!', isCorrect: true };
    }

    if (correctAnswer.acceptableVariations?.some(variation =>
      userResponse.toLowerCase().trim() === variation.toLowerCase().trim()
    )) {
      return { score: maxPoints, maxPoints, feedback: 'Correct!', isCorrect: true };
    }

    return { score: 0, maxPoints, feedback: 'Incorrect.', isCorrect: false };
  }

  scorePartial(scoring, userResponse) {
    const { partialCreditRules, maxPoints } = scoring.correctAnswer;
    let totalScore = 0;
    let feedback = [];

    partialCreditRules?.forEach(rule => {
      if (new RegExp(rule.pattern, 'i').test(userResponse)) {
        totalScore += rule.points;
        if (rule.feedback) feedback.push(rule.feedback);
      }
    });

    return {
      score: Math.min(totalScore, maxPoints),
      maxPoints,
      feedback: feedback.join('. '),
      isCorrect: totalScore >= maxPoints
    };
  }

  scoreOptions(scoring, userResponse) {
    const { optionScoring, maxPoints } = scoring;
    const selectedOptions = userResponse.split(',').map(opt => opt.trim());

    let totalScore = 0;

    optionScoring?.forEach(option => {
      const isSelected = selectedOptions.includes(option.option);
      if (isSelected) {
        totalScore += option.points;
      }
    });

    return {
      score: Math.max(0, Math.min(totalScore, maxPoints)),
      maxPoints,
      feedback: totalScore >= maxPoints ? 'Correct!' : 'Some selections are incorrect.',
      isCorrect: totalScore >= maxPoints
    };
  }

  // NEW: Score entire book response
  async scoreBookResponse(bookId, responseId) {
    try {
      const messages = await this.getBookMessages(bookId);
      const responseDoc = await getDoc(doc(db, 'pb-book-responses', responseId));

      if (!responseDoc.exists()) {
        throw new Error('Response not found');
      }

      const responseData = responseDoc.data();
      const userResponses = responseData.responses;

      let totalScore = 0;
      let totalMaxPoints = 0;
      const questionScores = {};

      for (const message of messages) {
        if (!message.scoring?.enabled) continue;

        const userResponse = userResponses[message.variable] || userResponses[message.messageId];
        if (!userResponse) continue;

        const score = await this.scoreResponse(message, userResponse);

        questionScores[message.messageId] = score;
        totalScore += score.score || 0;
        totalMaxPoints += score.maxPoints || 0;
      }

      const percentage = totalMaxPoints > 0 ? (totalScore / totalMaxPoints) * 100 : 0;

      await updateDoc(doc(db, 'pb-book-responses', responseId), {
        'scores.totalScore': totalScore,
        'scores.totalMaxPoints': totalMaxPoints,
        'scores.percentage': percentage,
        'scores.questionScores': questionScores,
        'scores.gradedAt': new Date(),
        'metadata.isGraded': true
      });

      return { totalScore, totalMaxPoints, percentage, questionScores };

    } catch (error) {
      console.error('Error scoring book response:', error);
      throw error;
    }
  }

  async getBookMessages(bookId) {
    try {
      if (!db) {
        throw new Error('Firestore is not initialized');
      }

      const q = query(
        collection(db, 'pb-messages'),
        where('bookId', '==', bookId),
        where('status', '==', 'active'),
        orderBy('order', 'asc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        firestoreDocId: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting book messages:', error);
      throw error;
    }
  }

  async updateBookMessages(bookId, messages) {
    try {
      if (!db) {
        throw new Error('Firestore is not initialized');
      }

      const batch = writeBatch(db);

      // First, delete existing messages for this book
      const existingQuery = query(
        collection(db, 'pb-messages'),
        where('bookId', '==', bookId)
      );
      const existingSnap = await getDocs(existingQuery);

      existingSnap.docs.forEach(doc => {
        batch.delete(doc.ref);
      });

      // Then add updated messages
      messages.forEach(message => {
        const docRef = doc(collection(db, 'pb-messages'));
        batch.set(docRef, {
          ...message,
          updatedAt: new Date()
        });
      });

      await batch.commit();
    } catch (error) {
      console.error('Error updating book messages:', error);
      throw error;
    }
  }

  // Analytics tracking
  async trackBookView(bookId, metadata = {}) {
    try {
      if (!db) {
        throw new Error('Firestore is not initialized');
      }

      await addDoc(collection(db, 'pb-analytics'), {
        bookId,
        type: 'view',
        timestamp: new Date(),
        ...metadata
      });
    } catch (error) {
      console.error('Error tracking book view:', error);
      // Don't throw error for analytics, just log it
    }
  }

  async trackInteraction(bookId, interactionData) {
    try {
      if (!db) {
        throw new Error('Firestore is not initialized');
      }

      await addDoc(collection(db, 'pb-analytics'), {
        bookId,
        type: 'interaction',
        timestamp: new Date(),
        ...interactionData
      });
    } catch (error) {
      console.error('Error tracking interaction:', error);
      // Don't throw error for analytics, just log it
    }
  }

  async deleteBook(bookId) {
    try {
      if (!db) {
        throw new Error('Firestore is not initialized');
      }

      const batch = writeBatch(db);

      // Delete book
      batch.delete(doc(db, 'pb-books', bookId));

      // Delete associated messages
      const messagesQuery = query(
        collection(db, 'pb-messages'),
        where('bookId', '==', bookId)
      );
      const messagesSnap = await getDocs(messagesQuery);

      messagesSnap.docs.forEach(doc => {
        batch.delete(doc.ref);
      });

      await batch.commit();
    } catch (error) {
      console.error('Error deleting book:', error);
      throw error;
    }
  }

  async createBookResponse(bookId, userId = null, sessionId = null, options = {}) {
    try {
      const responseId = `response_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      const bookDoc = await getDoc(doc(db, 'pb-books', bookId));
      if (!bookDoc.exists()) {
        throw new Error('Book not found');
      }

      const bookData = bookDoc.data();
      const bookOwnerId = bookData.creatorId || bookData.userId || bookData.ownerId;

      // Calculate timing constraints
      let timing = {};
      if (options.timeLimit) {
        const startTime = new Date();
        const endTime = new Date(startTime.getTime() + (options.timeLimit * 60 * 1000));
        timing = {
          timeLimit: options.timeLimit,
          startedAt: startTime,
          expiresAt: endTime,
          timeRemaining: options.timeLimit * 60
        };
      }

      // Get user email if userId exists
      const userEmail = userId ? await this.getUserEmail(userId) : null;

      const responseData = {
        responseId,
        bookId,
        userId: userId || null,
        sessionId: sessionId || null,
        bookOwnerId: bookOwnerId || null,
        bookOwnerEmail: bookData.creatorEmail || bookData.ownerEmail || null,
        userEmail: userEmail,
        responses: {},
        metadata: {
          isComplete: false,
          isSubmitted: false,
          canEdit: true,
          allowResumeIncomplete: options.allowResumeIncomplete !== false,
          ...timing
        },
        progress: {
          currentSection: null,
          completedSections: [],
          lastActiveAt: new Date()
        },
        access: {
          canView: [
            bookOwnerId,
            ...(userId ? [userId] : []),
            ...(bookData.collaborators || [])
          ].filter(Boolean),
          canEdit: [userId].filter(Boolean),
          isPublic: false,
          bookOwnerAccess: true
        },
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Clean undefined values and save directly
      const cleanedData = this.cleanData(responseData);
      await setDoc(doc(db, 'pb-book-responses', responseId), cleanedData);

      console.log('Book response created:', responseId);
      return { id: responseId, ...cleanedData };

    } catch (error) {
      console.error('Error creating book response:', error);
      throw error;
    }
  }

  // Get all responses for books owned by a specific user
  async getResponsesForBookOwner(ownerId) {
    try {
      const snapshot = await this.db.collection('pb-book-responses')
        .where('bookOwnerId', '==', ownerId)
        .orderBy('updatedAt', 'desc')
        .get();

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

    } catch (error) {
      console.error('Error getting responses for book owner:', error);
      return [];
    }
  }

  // Get responses for a specific book (only for owner/collaborators)
  async getBookResponses(bookId, requesterId, filters = {}) {
    try {
      const bookDoc = await getDoc(doc(db, 'pb-books', bookId));
      if (!bookDoc.exists()) {
        throw new Error('Book not found');
      }

      const bookData = bookDoc.data();
      const bookOwnerId = bookData.creatorId || bookData.userId || bookData.ownerId;
      const collaborators = bookData.collaborators || [];

      if (requesterId !== bookOwnerId && !collaborators.includes(requesterId)) {
        throw new Error('Access denied');
      }

      let q = query(
        collection(db, 'pb-book-responses'),
        where('bookId', '==', bookId),
        where('bookOwnerId', '==', bookOwnerId)
      );

      if (filters.isSubmitted !== undefined) {
        q = query(q, where('metadata.isSubmitted', '==', filters.isSubmitted));
      }
      if (filters.isComplete !== undefined) {
        q = query(q, where('metadata.isComplete', '==', filters.isComplete));
      }

      q = query(q, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

    } catch (error) {
      console.error('Error getting book responses:', error);
      throw error;
    }
  }

  async getBookResponse(bookId, userId = null, sessionId = null, options = {}) {
    try {
      let q = query(
        collection(db, 'pb-book-responses'),
        where('bookId', '==', bookId),
      );

      // Add filters
      if (userId && sessionId) {
        q = query(q, where('userId', '==', userId), where('sessionId', '==', sessionId));
      } else if (userId) {
        q = query(q, where('userId', '==', userId));
      } else if (sessionId) {
        q = query(q, where('sessionId', '==', sessionId));
      }

      q = query(q, orderBy('updatedAt', 'desc'));
      const snapshot = await getDocs(q);

      if (snapshot.empty) return null;

      const response = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };

      // Handle expired responses
      if (response.metadata?.expiresAt) {
        const now = new Date();
        const expiresAt = response.metadata.expiresAt.toDate();

        if (now > expiresAt && !response.metadata.isSubmitted) {
          await this.submitBookResponse(response.responseId, { autoSubmitted: true });
          response.metadata.isSubmitted = true;
          response.metadata.canEdit = false;
        } else if (now <= expiresAt) {
          response.metadata.timeRemaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
        }
      }

      return response;

    } catch (error) {
      console.error('Error getting book response:', error);
      return null;
    }
  }

  async updateBookResponse(responseId, updateData, options = {}) {
    try {
      const responseDoc = await getDoc(doc(db, 'pb-book-responses', responseId));
      if (!responseDoc.exists()) {
        throw new Error('Response not found');
      }

      const responseData = responseDoc.data();

      if (!responseData.metadata?.canEdit && !options.forceUpdate) {
        throw new Error('Response cannot be edited - it may be submitted or expired');
      }

      // Handle timing updates
      let updatedMetadata = { ...responseData.metadata };
      if (responseData.metadata?.expiresAt) {
        const now = new Date();
        const expiresAt = responseData.metadata.expiresAt.toDate();

        if (now > expiresAt && !responseData.metadata.isSubmitted) {
          updatedMetadata.isSubmitted = true;
          updatedMetadata.canEdit = false;
          updatedMetadata.timeRemaining = 0;
        } else if (now <= expiresAt) {
          updatedMetadata.timeRemaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
        }
      }

      const finalUpdateData = {
        ...updateData,
        metadata: {
          ...updatedMetadata,
          ...(updateData.metadata || {})
        },
        progress: {
          ...responseData.progress,
          ...(updateData.progress || {}),
          lastActiveAt: new Date()
        },
        updatedAt: new Date()
      };

      const cleanedData = this.cleanData(finalUpdateData);
      await updateDoc(doc(db, 'pb-book-responses', responseId), cleanedData);
      return cleanedData;

    } catch (error) {
      console.error('Error updating book response:', error);
      throw error;
    }
  }

  async submitBookResponse(responseId, submissionData = {}) {
    try {
      const responseDoc = await getDoc(doc(db, 'pb-book-responses', responseId));
      if (!responseDoc.exists()) {
        throw new Error('Response not found');
      }

      const responseData = responseDoc.data();

      const submissionMetadata = {
        ...responseData.metadata,
        isComplete: true,
        isSubmitted: true,
        canEdit: false,
        submittedAt: new Date(),
        timeRemaining: 0,
        finalSubmission: true,
        ...submissionData
      };

      const updateData = {
        metadata: submissionMetadata,
        updatedAt: new Date()
      };

      await updateDoc(doc(db, 'pb-book-responses', responseId), updateData);
      console.log('Response submitted successfully:', responseId);
      return { success: true, submittedAt: submissionMetadata.submittedAt };

    } catch (error) {
      console.error('Error submitting response:', error);
      throw error;
    }
  }

  // New method to enable response editing (for teachers/owners)
  async enableResponseEditing(responseId, ownerId, options = {}) {
    try {
      const responseDoc = await this.getDocument('pb-book-responses', responseId);
      if (!responseDoc.exists()) {
        throw new Error('Response not found');
      }

      const responseData = responseDoc.data();

      // Verify ownership
      if (responseData.bookOwnerId !== ownerId) {
        throw new Error('Only book owner can enable response editing');
      }

      const updatedMetadata = {
        ...responseData.metadata,
        canEdit: true,
        isSubmitted: false,
        editingEnabledAt: new Date(),
        editingEnabledBy: ownerId,
        ...options
      };

      await this.updateDocument('pb-book-responses', responseId, {
        metadata: updatedMetadata,
        updatedAt: new Date()
      });

      return { success: true };

    } catch (error) {
      console.error('Error enabling response editing:', error);
      throw error;
    }
  }

  // Dashboard method to get all books and their response counts for an owner
  async getOwnerDashboardData(ownerId) {
    try {
      // Get all books owned by this user
      const booksSnapshot = await this.db.collection('pb-books')
        .where('creatorId', '==', ownerId)
        .orderBy('createdAt', 'desc')
        .get();

      const books = booksSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Get response counts for each book
      const bookData = await Promise.all(
        books.map(async (book) => {
          const responsesSnapshot = await this.db.collection('pb-book-responses')
            .where('bookId', '==', book.id)
            .where('bookOwnerId', '==', ownerId)
            .get();

          const responses = responsesSnapshot.docs.map(doc => doc.data());

          return {
            ...book,
            responseCount: responses.length,
            completedCount: responses.filter(r => r.metadata?.isComplete).length,
            lastResponseDate: responses.length > 0
              ? Math.max(...responses.map(r => r.updatedAt?.toDate?.()?.getTime() || 0))
              : null
          };
        })
      );

      return bookData;

    } catch (error) {
      console.error('Error getting owner dashboard data:', error);
      return [];
    }
  }

  // Method to add collaborators to a book (affects future response access)
  async addBookCollaborator(bookId, ownerId, collaboratorEmail) {
    try {
      // Verify ownership
      const bookDoc = await this.getDocument('pb-books', bookId);
      if (!bookDoc.exists()) {
        throw new Error('Book not found');
      }

      const bookData = bookDoc.data();
      if (bookData.creatorId !== ownerId) {
        throw new Error('Only book owner can add collaborators');
      }

      // Get collaborator userId by email
      const usersSnapshot = await this.db.collection('users')
        .where('email', '==', collaboratorEmail)
        .limit(1)
        .get();

      if (usersSnapshot.empty) {
        throw new Error('User not found with that email');
      }

      const collaboratorId = usersSnapshot.docs[0].id;
      const currentCollaborators = bookData.collaborators || [];

      if (!currentCollaborators.includes(collaboratorId)) {
        currentCollaborators.push(collaboratorId);

        // Update book
        await this.updateDocument('pb-books', bookId, {
          collaborators: currentCollaborators,
          updatedAt: new Date()
        });

        // Update existing responses to give access to new collaborator
        const responsesSnapshot = await this.db.collection('pb-book-responses')
          .where('bookId', '==', bookId)
          .get();

        const batch = this.db.batch();
        responsesSnapshot.docs.forEach(doc => {
          const currentCanView = doc.data().access?.canView || [];
          if (!currentCanView.includes(collaboratorId)) {
            batch.update(doc.ref, {
              'access.canView': [...currentCanView, collaboratorId],
              'bookCollaborators': currentCollaborators,
              updatedAt: new Date()
            });
          }
        });

        await batch.commit();

        console.log('Collaborator added successfully:', collaboratorEmail);
        return true;
      }

      return false; // Already a collaborator

    } catch (error) {
      console.error('Error adding collaborator:', error);
      throw error;
    }
  }

  async uploadFile(file, path, options = {}) {
    try {

      // Create unique filename
      const timestamp = Date.now();
      const cleanFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = `${path}/${timestamp}_${cleanFileName}`;

      console.log(`Uploading file to: ${fileName}`);

      // Upload to Firebase Storage
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      console.log(`Upload successful: ${downloadURL}`);

      return {
        url: downloadURL,
        fileName: cleanFileName,
        originalFileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        uploadedAt: new Date().toISOString(),
        path: fileName
      };

    } catch (error) {
      console.error('Error uploading file:', error);

      // Provide user-friendly error messages
      if (error.code === 'storage/unauthorized') {
        throw new Error('Upload permission denied. Please check your access rights.');
      } else if (error.code === 'storage/canceled') {
        throw new Error('Upload was canceled');
      } else if (error.code === 'storage/quota-exceeded') {
        throw new Error('Storage quota exceeded');
      } else if (error.code === 'storage/invalid-argument') {
        throw new Error('Invalid file or upload parameters');
      } else if (error.message.includes('Firebase ID token has expired')) {
        throw new Error('Session expired. Please refresh the page and try again.');
      } else {
        throw new Error(error.message || 'Upload failed');
      }
    }
  }

  async uploadEventFile(file, bookId, fileType, options = {}) {
    const basePath = `event-books/${bookId}/events`;
    const path = `${basePath}/${fileType}`;

    return this.uploadFile(file, path, {
      maxSize: 10 * 1024 * 1024, // 10MB
      allowedTypes: [
        'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'
      ],
      ...options
    });
  }

  async uploadEventGalleryMedia(file, bookId, galleryId = 'main') {
    const path = `pb-${bookId}/events/galleries/${galleryId}`;
    return this.uploadFile(file, path, {
      maxSize: 20 * 1024 * 1024, // 20MB for gallery media
      allowedTypes: [
        'image/jpeg', 'image/png', 'image/gif', 'image/webp',
        'video/mp4', 'video/webm', 'video/ogg'
      ]
    });
  }

  async uploadGiftRegistryImage(file, bookId, giftId) {
    const path = `pb-${bookId}/events/gifts/${giftId}`;
    return this.uploadFile(file, path, {
      maxSize: 5 * 1024 * 1024, // 5MB for gift images
      allowedTypes: [
        'image/jpeg', 'image/png', 'image/gif', 'image/webp'
      ]
    });
  }

  async uploadEventLogo(file, bookId) {
    return this.uploadEventFile(file, bookId, 'logo', {
      maxSize: 5 * 1024 * 1024, // 5MB for logos
    });
  }

  async uploadEventHeroImage(file, bookId) {
    return this.uploadEventFile(file, bookId, 'hero', {
      maxSize: 15 * 1024 * 1024, // 15MB for hero images
    });
  }



  async uploadMultipleFiles(files, uploadFunction, onProgress) {
    const results = [];

    for (let i = 0; i < files.length; i++) {
      try {
        const result = await uploadFunction(files[i]);
        results.push({ success: true, index: i, ...result });

        if (onProgress) {
          onProgress(i + 1, files.length, result);
        }
      } catch (error) {
        console.error(`Error uploading file ${files[i].name}:`, error);
        results.push({
          success: false,
          index: i,
          error: error.message,
          fileName: files[i].name
        });

        if (onProgress) {
          onProgress(i + 1, files.length, null, error);
        }
      }
    }

    return results;
  }

  async deleteFile(fileUrl) {
    try {
      if (fileUrl && fileUrl.includes('firebase')) {
        const fileRef = ref(storage, fileUrl);
        await deleteObject(fileRef);
        return { success: true };
      }
      return { success: true, message: 'No file to delete' };
    } catch (error) {
      console.error('Error deleting file:', error);
      return { success: false, error: error.message };
    }
  }


  async uploadResponseFile(responseId, fieldName, file, bookId = null) {
    try {
      console.log('Uploading response file:', { responseId, fieldName, fileName: file.name });

      // Sanitize filename
      const sanitizedFileName = this.sanitizeFileName(file.name);

      // Create storage path - using bookId for better organization if available
      const storagePath = bookId
        ? `pb-book-responses/${bookId}/${responseId}/${fieldName}/${sanitizedFileName}`
        : `pb-book-responses/${responseId}/${fieldName}/${sanitizedFileName}`;

      const storageRef = this.storage.ref(storagePath);

      // Add metadata
      const metadata = {
        contentType: file.type,
        customMetadata: {
          responseId,
          fieldName,
          originalName: file.name,
          uploadTime: new Date().toISOString()
        }
      };

      // Upload file
      const uploadTask = storageRef.put(file, metadata);

      // Return promise with progress tracking
      return new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log(`Upload progress: ${progress}%`);
            // You can emit progress events here if needed
          },
          (error) => {
            console.error('Upload error:', error);
            reject(error);
          },
          async () => {
            try {
              const downloadURL = await uploadTask.snapshot.ref.getDownloadURL();
              console.log('File uploaded successfully:', downloadURL);

              // Return file info
              resolve({
                downloadURL,
                fileName: sanitizedFileName,
                originalName: file.name,
                size: file.size,
                contentType: file.type,
                storagePath
              });
            } catch (error) {
              reject(error);
            }
          }
        );
      });

    } catch (error) {
      console.error('Error uploading response file:', error);
      throw error;
    }
  }

  // Delete response file
  async deleteResponseFile(storagePath) {
    try {
      const storageRef = this.storage.ref(storagePath);
      await storageRef.delete();
      console.log('File deleted:', storagePath);
      return true;
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  }

  // Get all files for a response
  async getResponseFiles(responseId, bookId = null) {
    try {
      const basePath = bookId
        ? `pb-book-responses/${bookId}/${responseId}/`
        : `pb-book-responses/${responseId}/`;

      const storageRef = this.storage.ref(basePath);
      const result = await storageRef.listAll();

      const files = await Promise.all(
        result.items.map(async (itemRef) => {
          const downloadURL = await itemRef.getDownloadURL();
          const metadata = await itemRef.getMetadata();

          return {
            downloadURL,
            fileName: itemRef.name,
            originalName: metadata.customMetadata?.originalName || itemRef.name,
            size: metadata.size,
            contentType: metadata.contentType,
            storagePath: itemRef.fullPath,
            uploadTime: metadata.customMetadata?.uploadTime
          };
        })
      );

      return files;
    } catch (error) {
      console.error('Error getting response files:', error);
      return [];
    }
  }

  async updateBookResponseWithFile(responseId, fieldName, fileInfo, additionalData = {}) {
    try {
      const updateData = {
        [`responses.${fieldName}`]: fileInfo.downloadURL,
        [`responses.${fieldName}_metadata`]: {
          fileName: fileInfo.fileName,
          originalName: fileInfo.originalName,
          size: fileInfo.size,
          contentType: fileInfo.contentType,
          storagePath: fileInfo.storagePath,
          uploadTime: fileInfo.uploadTime || new Date().toISOString()
        },
        ...additionalData,
        updatedAt: new Date()
      };

      await this.updateDocument('pb-book-responses', responseId, updateData);

      console.log('Response updated with file info:', { responseId, fieldName });
      return updateData;

    } catch (error) {
      console.error('Error updating response with file:', error);
      throw error;
    }
  }

  // Clean up orphaned files when response is deleted
  async deleteResponse(responseId, bookId = null) {
    try {
      // Get all files for this response
      const files = await this.getResponseFiles(responseId, bookId);

      // Delete all files
      await Promise.all(
        files.map(file => this.deleteResponseFile(file.storagePath))
      );

      // Delete the response document
      await this.deleteDocument('pb-book-responses', responseId);

      console.log('Response and associated files deleted:', responseId);
      return true;

    } catch (error) {
      console.error('Error deleting response:', error);
      throw error;
    }
  }

  // Utility method to sanitize filenames
  sanitizeFileName(fileName) {
    const timestamp = Date.now();
    const extension = fileName.split('.').pop();
    const nameWithoutExt = fileName.replace(/\.[^/.]+$/, "");

    const sanitized = nameWithoutExt
      .replace(/[^a-zA-Z0-9]/g, '_')
      .replace(/_+/g, '_')
      .toLowerCase()
      .substring(0, 50); // Limit length

    return `${sanitized}_${timestamp}.${extension}`;
  }

  // Get download URLs for book owner to access all response files
  async getAllBookResponseFiles(bookId, ownerId) {
    try {
      // Verify ownership
      const bookDoc = await this.getDocument('pb-books', bookId);
      if (!bookDoc.exists() || bookDoc.data().creatorId !== ownerId) {
        throw new Error('Access denied');
      }

      // Get all responses for the book
      const responses = await this.getBookResponses(bookId, ownerId);

      // Get files for each response
      const allFiles = await Promise.all(
        responses.map(async (response) => {
          const files = await this.getResponseFiles(response.responseId, bookId);
          return {
            responseId: response.responseId,
            userEmail: response.userEmail || 'Anonymous',
            files
          };
        })
      );

      return allFiles.filter(item => item.files.length > 0);

    } catch (error) {
      console.error('Error getting all book response files:', error);
      throw error;
    }
  }

  async updateResponseProgress(responseId, section, isComplete = false) {
    try {
      const docRef = doc(db, 'pb-book-responses', responseId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Response not found');
      }

      const currentData = docSnap.data();
      const completedSections = currentData.metadata?.completedSections || [];

      if (section && !completedSections.includes(section)) {
        completedSections.push(section);
      }

      const updateData = {
        'metadata.completedSections': completedSections,
        'metadata.currentSection': section,
        'metadata.isComplete': isComplete,
        'metadata.lastUpdated': new Date(),
        updatedAt: new Date()
      };

      await updateDoc(docRef, updateData);
      console.log('Response progress updated:', { responseId, section, isComplete });

    } catch (error) {
      console.error('Error updating response progress:', error);
      throw error;
    }
  }

  // Utility methods for response system
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  getDeviceInfo() {
    // Ensure we're in a browser environment
    if (typeof window === 'undefined') {
      return {
        userAgent: 'server',
        screenSize: 'unknown',
        platform: 'server',
        language: 'unknown',
        environment: 'server'
      };
    }

    try {
      return {
        userAgent: navigator?.userAgent || 'unknown',
        screenSize: window.screen ?
          `${window.screen.width}x${window.screen.height}` :
          'unknown',
        platform: navigator?.platform || 'unknown',
        language: navigator?.language || 'unknown',
        environment: 'browser',
        // Additional useful info
        viewport: window.innerWidth ?
          `${window.innerWidth}x${window.innerHeight}` :
          'unknown',
        timezone: Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone || 'unknown'
      };
    } catch (error) {
      console.warn('Error getting device info:', error);
      return {
        userAgent: 'unknown',
        screenSize: 'unknown',
        platform: 'unknown',
        language: 'unknown',
        environment: 'browser-error'
      };
    }
  }

  // Analytics for responses (using your existing tracking pattern)
  async getBookAnalytics(bookId, creatorId) {
    try {
      const responses = await this.getBookResponses(bookId, creatorId);

      const analytics = {
        totalResponses: responses.length,
        completedResponses: responses.filter(r => r.metadata?.isComplete).length,
        averageTimeSpent: 0,
        popularSections: {},
        responseDistribution: {},
      };

      // Calculate analytics
      let totalTime = 0;
      const sectionCounts = {};
      const responseCounts = {};

      responses.forEach(response => {
        // Time analytics
        if (response.metadata?.totalTimeSpent) {
          totalTime += response.metadata.totalTimeSpent;
        }

        // Section analytics
        response.metadata?.completedSections?.forEach(section => {
          sectionCounts[section] = (sectionCounts[section] || 0) + 1;
        });

        // Response analytics
        Object.entries(response.responses || {}).forEach(([key, value]) => {
          if (!key.includes('_metadata')) { // Skip metadata responses
            if (!responseCounts[key]) {
              responseCounts[key] = {};
            }
            responseCounts[key][value] = (responseCounts[key][value] || 0) + 1;
          }
        });
      });

      analytics.averageTimeSpent = responses.length > 0 ? totalTime / responses.length : 0;
      analytics.popularSections = sectionCounts;
      analytics.responseDistribution = responseCounts;

      return analytics;

    } catch (error) {
      console.error('Error getting book analytics:', error);
      return null;
    }
  }

  // Delete response (using your existing delete pattern)
  async deleteBookResponse(responseId) {
    try {
      if (!db) {
        throw new Error('Firestore is not initialized');
      }

      await deleteDoc(doc(db, 'pb-book-responses', responseId));
      console.log('Book response deleted:', responseId);
      return true;

    } catch (error) {
      console.error('Error deleting book response:', error);
      throw error;
    }
  }

  // Get responses for dashboard (using your existing patterns)
  async getUserBookResponses(userId) {
    try {
      if (!db) {
        throw new Error('Firestore is not initialized');
      }

      const q = query(
        collection(db, 'pb-book-responses'),
        where('userId', '==', userId),
        orderBy('updatedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

    } catch (error) {
      console.error('Error getting user book responses:', error);
      return [];
    }
  }

  // Batch operations for responses (using your existing batch pattern)
  async batchUpdateResponses(updates) {
    try {
      if (!db) {
        throw new Error('Firestore is not initialized');
      }

      const batch = writeBatch(db);

      updates.forEach(({ responseId, data }) => {
        const docRef = doc(db, 'pb-book-responses', responseId);
        batch.update(docRef, {
          ...data,
          updatedAt: new Date()
        });
      });

      await batch.commit();
      console.log('Batch response updates completed');

    } catch (error) {
      console.error('Error batch updating responses:', error);
      throw error;
    }
  }

  getResponseValue(responses, messageId, variable) {
    // Priority: variable > messageId
    if (variable && responses[variable] !== undefined) {
      return responses[variable];
    }

    if (responses[messageId] !== undefined) {
      return responses[messageId];
    }

    return null;
  }

  async createAnalyticsEvent(eventData) {
    try {
      const analyticsDoc = {
        ...eventData,
        timestamp: new Date().toISOString(),
        createdAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, 'pb-booking-analytics'), analyticsDoc);

      return { id: docRef.id, ...analyticsDoc };
    } catch (error) {
      console.error('Error creating analytics event:', error);
      throw error;
    }
  }

  async getAnalytics(bookId, queryOptions = {}) {
    try {
      const conditions = [['bookId', '==', bookId]];

      // Add additional filters if provided
      if (queryOptions.event) {
        conditions.push(['event', '==', queryOptions.event]);
      }

      if (queryOptions.bookingType) {
        conditions.push(['bookingType', '==', queryOptions.bookingType]);
      }

      if (queryOptions.dateFrom) {
        conditions.push(['timestamp', '>=', queryOptions.dateFrom]);
      }

      if (queryOptions.dateTo) {
        conditions.push(['timestamp', '<=', queryOptions.dateTo]);
      }

      return await this.getDocuments('pb-booking-analytics', {
        where: conditions,
        orderBy: ['timestamp', 'desc'],
        limit: queryOptions.limit || 100
      });
    } catch (error) {
      console.error('Error getting analytics:', error);
      return [];
    }
  }

  // Updated createBookingRequest method to use analytics method
  async recordBookingRequest(bookId, requestData) {
    try {
      const requestDoc = {
        ...requestData,
        bookId,
        status: 'pending',
        source: 'booking_request_form',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, 'pb-booking-requests'), requestDoc);

      // Use the new analytics method
      await this.createAnalyticsEvent({
        bookId,
        event: 'booking_request_sent',
        bookingType: requestData.bookingType,
        metadata: {
          requestType: requestData.requestType,
          isLocationFlexible: requestData.isLocationFlexible,
          isDateFlexible: requestData.isDateFlexible,
          requestId: docRef.id
        }
      });

      return { id: docRef.id, ...requestDoc };
    } catch (error) {
      console.error('Error creating booking request:', error);
      throw error;
    }
  }

  async getDocuments(collectionName, queryOptions = {}) {
    try {
      let q = collection(db, collectionName);

      // Apply where conditions
      if (queryOptions.where) {
        queryOptions.where.forEach(condition => {
          const [field, operator, value] = condition;
          q = query(q, where(field, operator, value));
        });
      }

      // Apply ordering
      if (queryOptions.orderBy) {
        const [field, direction = 'asc'] = queryOptions.orderBy;
        q = query(q, orderBy(field, direction));
      }

      // Apply limit
      if (queryOptions.limit) {
        q = query(q, limit(queryOptions.limit));
      }

      const querySnapshot = await getDocs(q);
      const documents = [];

      querySnapshot.forEach((doc) => {
        documents.push({ id: doc.id, ...doc.data() });
      });

      return documents;
    } catch (error) {
      console.error(`Error getting documents from ${collectionName}:`, error);
      throw error;
    }
  }

  // Method for registering vendors
  async registerVendor(vendorData) {
    try {
      const vendorDoc = {
        ...vendorData,
        status: 'pending', // pending approval
        acceptingRequests: false, // disabled until approved
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, 'pb-vendors'), vendorDoc);

      return { id: docRef.id, ...vendorDoc };
    } catch (error) {
      console.error('Error registering vendor:', error);
      throw error;
    }
  }

  // Method for getting vendors by location and service type
  async getVendorsByService(serviceType, location = null) {
    try {
      const conditions = [
        ['serviceType', '==', serviceType],
        ['status', '==', 'active'],
        ['acceptingRequests', '==', true]
      ];

      if (location && location !== 'flexible') {
        conditions.push(['serviceLocations', 'array-contains', location]);
      }

      return await this.getDocuments('pb-vendors', {
        where: conditions,
        orderBy: ['rating', 'desc']
      });
    } catch (error) {
      console.error('Error getting vendors by service:', error);
      return [];
    }
  }
}

export const firestoreService = new FirestoreService();